import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Box from '@material-ui/core/Box';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import Tooltip from '@material-ui/core/Tooltip';
import HelpIcon from '@material-ui/icons/Help';
import Divider from '@material-ui/core/Divider';
import { ForecastType, MetricType } from './types';

const useStyles = makeStyles(theme => ({
  section: {
    padding: theme.spacing(3)
  },
  helpIcon: {
    fontSize: '22px',
    marginLeft: theme.spacing(1),
  },
  labelWithHelp: {
    display: 'flex',
    alignItems: 'center'
  },
  divider: {
    margin: theme.spacing(3, 0)
  }
}));

interface ForecastSettingsSectionProps {
  // Historical Data props
  metricType: MetricType;
  throughputData: string;
  onMetricTypeChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onThroughputDataChange: (value: string) => void;
  onPopulateTestData: () => void;
  
  // Forecast props
  forecastType: ForecastType;
  numberOfSprints: string;
  numberOfItems: string;
  isCalculating: boolean;
  onForecastTypeChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onNumberOfSprintsChange: (value: string) => void;
  onNumberOfItemsChange: (value: string) => void;
}

const ForecastSettingsSection: React.FC<ForecastSettingsSectionProps> = ({
  // Historical Data props
  metricType,
  throughputData,
  onMetricTypeChange,
  onThroughputDataChange,
  onPopulateTestData,
  
  // Forecast props
  forecastType,
  numberOfSprints,
  numberOfItems,
  isCalculating,
  onForecastTypeChange,
  onNumberOfSprintsChange,
  onNumberOfItemsChange
}) => {
  const classes = useStyles();

  return (
    <Paper className={classes.section}>
      <Typography variant="h5" gutterBottom>
        Forecast Settings
      </Typography>

      {/* Historical Data Section */}
      <Box>
        <Box my={2}>
          <FormControl component="fieldset">
            <FormLabel component="legend"><strong>Metric Type</strong></FormLabel>
            <RadioGroup
              value={metricType}
              onChange={onMetricTypeChange}
            >
              <FormControlLabel
                value="throughput"
                control={<Radio />}
                label={
                  <div className={classes.labelWithHelp}>
                    Throughput (items)
                    <Tooltip placement="right" title="Throughput is the number of items that were completed in a given sprint.">
                      <HelpIcon className={classes.helpIcon} color='primary' />
                    </Tooltip>
                  </div>
                }
              />
              <FormControlLabel
                value="velocity"
                control={<Radio />}
                label={
                  <div className={classes.labelWithHelp}>
                    Velocity (story points)
                    <Tooltip placement="right" title="Velocity is the sum of story points that were completed in a given sprint.">
                      <HelpIcon className={classes.helpIcon} color='primary' />
                    </Tooltip>
                  </div>
                }
              />
            </RadioGroup>
          </FormControl>
        </Box>
        
        <Box my={2}>
          <TextField
            label={`Historical ${metricType === 'throughput' ? 'Throughput' : 'Velocity'} (comma-separated)`}
            value={throughputData}
            onChange={(e) => onThroughputDataChange(e.target.value)}
            fullWidth
            placeholder={'eg. 5,7,3,5,22,6'}
            variant="outlined"
          />
        </Box>
        
        <Box my={2}>
          <Button 
            variant="outlined" 
            color="primary" 
            fullWidth
            onClick={onPopulateTestData}
          >
            Populate with test data
          </Button>
        </Box>
      </Box>

      <Divider className={classes.divider} />

      {/* Forecast Section */}
      <Box>
        <Box my={2}>
          <FormControl component="fieldset">
            <FormLabel component="legend"><strong>Forecast Type</strong></FormLabel>
            <RadioGroup
              value={forecastType}
              onChange={onForecastTypeChange}
            >
              <FormControlLabel
                value="items-in-sprints"
                control={<Radio />}
                label={
                  <div className={classes.labelWithHelp}>
                    # Items in X Sprints
                    <Tooltip placement="right" title={`Predict how many ${metricType === 'throughput' ? 'items' : 'story points'} can likely be completed in a given number of sprints`}>
                      <HelpIcon className={classes.helpIcon} color='primary' />
                    </Tooltip>
                  </div>
                }
              />
              <FormControlLabel
                value="sprints-for-items"
                control={<Radio />}
                label={
                  <div className={classes.labelWithHelp}>
                    {`# Sprints for X ${metricType === 'throughput' ? 'Items' : 'Story Points'}`}
                    <Tooltip placement="right" title={`Predict how many sprints it will take to complete a given number of ${metricType === 'throughput' ? 'items' : 'story points'}`}>
                      <HelpIcon className={classes.helpIcon} color='primary' />
                    </Tooltip>
                  </div>
                }
              />
            </RadioGroup>
          </FormControl>
        </Box>

        <Box my={2}>
          {forecastType === 'items-in-sprints' ? (
            <TextField
              label="Number of Sprints"
              type="number"
              value={numberOfSprints}
              onChange={(e) => onNumberOfSprintsChange(e.target.value)}
              fullWidth
              variant="outlined"
            />
          ) : (
            <TextField
              label={`Number of ${metricType === 'throughput' ? 'Items' : 'Story Points'}`}
              type="number"
              value={numberOfItems}
              onChange={(e) => onNumberOfItemsChange(e.target.value)}
              fullWidth
              variant="outlined"
            />
          )}
        </Box>

      </Box>
    </Paper>
  );
};

export default ForecastSettingsSection;
